<template>
	<div class="cms-post-meta" :class="['cms-post-meta--' + (size || 'small')]" :style="{ textAlign: align || 'left' }">
		<template v-for="(part, i) in parts">
			<span v-if="i > 0" :key="'sep-' + i" class="cms-post-meta__sep" aria-hidden="true">{{ separator || '·' }}</span>
			<a v-if="part.url" :key="'p-' + i" :href="part.url" class="cms-post-meta__part" :class="'cms-post-meta__part--' + part.kind">{{ part.text }}</a>
			<span v-else :key="'p-' + i" class="cms-post-meta__part" :class="'cms-post-meta__part--' + part.kind">{{ part.text }}</span>
		</template>
	</div>
</template>

<script>
/**
 * PostMetaBlock.vue
 * ─────────────────
 * The byline of the article: category · author · date · reading time, each
 * switchable, for Article templates. Category and author link to their archive
 * pages. Reads the post as the other Post blocks do (blocks/shared/postContext.js).
 */
import { isTrue } from '@/js/utilities/boolean/conditional';
import { resolvePost, postCategory, postAuthor, postDateLabel, postReadingTime } from '@/websites/cms/blocks/shared/postContext.js';

export default {
	name: 'PostMetaBlock',

	inject: {
		cmsPostPreview: { default: null }
	},

	props: {
		showCategory: { type: [Boolean, String], default: true },
		showAuthor: { type: [Boolean, String], default: true },
		showDate: { type: [Boolean, String], default: true },
		showReadingTime: { type: [Boolean, String], default: false },
		dateFormat: { type: String, default: 'long' },
		authorPrefix: { type: String, default: 'By' },
		separator: { type: String, default: '·' },
		align: { type: String, default: 'left' },
		size: { type: String, default: 'small' },
		post: { type: Object, default: null }
	},

	computed: {
		resolved: function () {
			return resolvePost(this.post, this.cmsPostPreview);
		},

		/** @returns {Array<{kind: string, text: string, url: string}>} */
		parts: function () {
			var post = this.resolved;
			var out = [];
			var cat = isTrue(this.showCategory) ? postCategory(post) : null;
			if (cat && cat.label) out.push({ kind: 'category', text: cat.label, url: cat.url });
			var author = isTrue(this.showAuthor) ? postAuthor(post) : null;
			if (author && author.label) out.push({ kind: 'author', text: (this.authorPrefix ? this.authorPrefix + ' ' : '') + author.label, url: author.url });
			var date = isTrue(this.showDate) ? postDateLabel(post, this.dateFormat) : '';
			if (date) out.push({ kind: 'date', text: date, url: '' });
			var minutes = isTrue(this.showReadingTime) ? postReadingTime(post) : 0;
			if (minutes) out.push({ kind: 'reading', text: minutes + ' min read', url: '' });
			return out;
		}
	}
};
</script>

<style scoped>
.cms-post-meta {
	display: block;
	line-height: 1.5;
	color: var(--site-body-color, #5b6472);
}
.cms-post-meta--small { font-size: .875em; }
.cms-post-meta--normal { font-size: 1em; }
.cms-post-meta__sep { opacity: .5; margin: 0 .5em; }
.cms-post-meta__part { color: inherit; text-decoration: none; }
a.cms-post-meta__part:hover { text-decoration: underline; }
.cms-post-meta__part--category {
	color: var(--site-accent-color, #FB6A00);
	font-weight: 700;
	letter-spacing: .06em;
	text-transform: uppercase;
	font-size: .9em;
}
.cms-post-meta__part--author { font-weight: 600; color: var(--site-heading-color, inherit); }
</style>
